
import React, { useState } from 'react';
import { GoogleGenAI } from '@google/genai';
import { AIPersona, UserProfile } from '../types';
import { getStudioApiKey } from '../utils/audio';

interface PersonaEngineProps {
  ui: any;
  onSave: (persona: AIPersona, user: UserProfile) => void;
  onCancel: () => void;
}

const LANGUAGES = ['English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Japanese', 'Mandarin'];
const ACCENTS = ['Universal', 'US', 'UK', 'Australian', 'European', 'Latin American'];

const PersonaEngine: React.FC<PersonaEngineProps> = ({ ui, onSave, onCancel }) => {
  const t = ui?.persona || { step0: "Identity", trait_cats: {}, interest_cats: {}, months: [] };
  const common = ui?.common || { next: "Next", back: "Back" };
  
  const [step, setStep] = useState(0); 
  const [isGeneratingAvatar, setIsGeneratingAvatar] = useState(false);
  const apiKey = getStudioApiKey();
  
  const [userProfile, setUserProfile] = useState<UserProfile>(() => {
      const saved = localStorage.getItem('groovefx_profile');
      try {
        return saved ? JSON.parse(saved) : {
          name: '', birthdayDay: '1', birthdayMonth: 'January', city: '', region: '', country: '', language: 'English', siteLanguage: 'English', accent: 'Universal', subscriptionTier: 'Free'
        };
      } catch(e) { return { name: '', birthdayDay: '1', birthdayMonth: 'January', city: '', region: '', country: '', language: 'English', accent: 'Universal', subscriptionTier: 'Free' }; }
  });
  
  const [persona, setPersona] = useState<Partial<AIPersona>>(() => {
      const saved = localStorage.getItem('groovefx_persona');
      try {
        return saved ? JSON.parse(saved) : {
          traits: [], interests: [], style: 'human-like', language: 'English', accent: 'Australian', gender: 'Female', name: 'Eve', avatar: ''
        };
      } catch(e) { return { traits: [], interests: [], style: 'human-like', language: 'English', accent: 'Australian', gender: 'Female', name: 'Eve', avatar: '' }; }
  });

  const generateDays = () => Array.from({length: 31}, (_, i) => (i + 1).toString());

  const generateAvatar = async () => {
    if (!apiKey) return;
    setIsGeneratingAvatar(true);
    try {
      const ai = new GoogleGenAI({ apiKey });
      const prompt = `Hyper-realistic, cinematic studio portrait headshot of an AI Agent named ${persona.name}. 
                      Directive context: ${persona.background}. 
                      Neural traits: ${persona.traits?.join(", ")}. 
                      Artistic style: Creative Sydney studio, high-fidelity bokeh, 8k resolution, centered manifestation.`;
      
      const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash-image',
        contents: { parts: [{ text: prompt }] },
      });

      const parts = response.candidates?.[0]?.content?.parts || [];
      for (const part of parts) {
        if (part.inlineData) {
          const base64 = `data:image/png;base64,${part.inlineData.data}`;
          setPersona(prev => ({ ...prev, avatar: base64 }));
          break;
        }
      }
    } catch (e) {
      console.error("Avatar Manifestation Aborted:", e);
    } finally {
      setIsGeneratingAvatar(false);
    }
  };

  const getStepTitle = () => {
    const titles = [t.step0, t.step1, t.step2, t.step3, t.step4, t.step5];
    return titles[step] || "Calibration";
  };

  return (
    <div className="w-full flex flex-col bg-black/40 rounded-[5rem] border border-white/10 overflow-hidden backdrop-blur-3xl shadow-3xl text-left min-h-[800px] animate-in fade-in duration-1000">
      <div className="p-12 md:p-20 border-b border-white/10 flex justify-between items-center bg-gradient-to-r from-groove-accent/10 to-transparent">
        <div className="space-y-6 text-left">
          <h2 className="text-5xl md:text-7xl font-black text-white tracking-tighter leading-none uppercase">
             {getStepTitle()}
          </h2>
          <p className="text-slate-500 text-[11px] font-black uppercase tracking-[0.6em]">Protocol Cycle 0{step + 1} // Sync Status</p>
        </div>
        <button onClick={onCancel} className="w-20 h-20 rounded-full border border-white/10 flex items-center justify-center text-white hover:text-groove-pink hover:scale-110 transition-all shadow-2xl">
          <i className="fa-solid fa-xmark text-3xl"></i>
        </button>
      </div>

      <div className="flex-1 p-12 lg:p-24 overflow-y-auto custom-scrollbar">
        {step === 0 && (
          <div className="max-w-4xl mx-auto space-y-20">
             <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                <div className="space-y-8">
                    <label className="text-[12px] font-black uppercase tracking-widest text-groove-accent">{t.name_label || "Your Name"}</label>
                    <input type="text" className="w-full bg-white/5 border border-white/10 rounded-[2rem] px-10 py-7 text-2xl text-white outline-none focus:border-groove-pink shadow-inner transition-all" value={userProfile.name} onChange={e => setUserProfile({...userProfile, name: e.target.value})} />
                </div>
                <div className="space-y-8">
                    <label className="text-[12px] font-black uppercase tracking-widest text-groove-accent">Birthday</label>
                    <div className="flex gap-8">
                      <select className="flex-1 bg-[#0a081a] border border-white/10 rounded-[2rem] px-8 py-7 text-white outline-none" value={userProfile.birthdayDay} onChange={e => setUserProfile({...userProfile, birthdayDay: e.target.value})}>
                        {generateDays().map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <select className="flex-[2] bg-[#0a081a] border border-white/10 rounded-[2rem] px-8 py-7 text-white outline-none" value={userProfile.birthdayMonth} onChange={e => setUserProfile({...userProfile, birthdayMonth: e.target.value})}>
                        {(t.months || []).map((m: string) => <option key={m} value={m}>{m}</option>)}
                      </select>
                    </div>
                </div>
                <div className="space-y-8">
                    <label className="text-[12px] font-black uppercase tracking-widest text-groove-accent">Language</label>
                    <select className="w-full bg-[#0a081a] border border-white/10 rounded-[2rem] px-10 py-7 text-2xl text-white outline-none" value={userProfile.language} onChange={e => setUserProfile({...userProfile, language: e.target.value})}>
                      {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                </div>
                <div className="space-y-8">
                    <label className="text-[12px] font-black uppercase tracking-widest text-groove-accent">Accent</label>
                    <select className="w-full bg-[#0a081a] border border-white/10 rounded-[2rem] px-10 py-7 text-2xl text-white outline-none" value={userProfile.accent} onChange={e => setUserProfile({...userProfile, accent: e.target.value})}>
                      {ACCENTS.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>
             </div>
             <div className="pt-20 text-center">
                <button onClick={() => setStep(1)} disabled={!userProfile.name} className="px-24 py-10 bg-white text-black font-black rounded-[3rem] uppercase tracking-[0.6em] shadow-3xl hover:bg-groove-pink hover:text-white hover:scale-105 active:scale-95 transition-all disabled:opacity-20 text-sm">{common.next}</button>
             </div>
          </div>
        )}

        {step === 1 && (
          <div className="max-w-4xl mx-auto space-y-20">
             <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                <div className="space-y-8">
                    <label className="text-[12px] font-black uppercase tracking-widest text-groove-accent">{t.partner_name || "Agent Name"}</label>
                    <input type="text" className="w-full bg-white/5 border border-white/10 rounded-[2rem] px-10 py-7 text-2xl text-white outline-none focus:border-groove-pink shadow-inner transition-all" value={persona.name || ''} onChange={e => setPersona({...persona, name: e.target.value})} />
                </div>
                <div className="space-y-8">
                    <label className="text-[12px] font-black uppercase tracking-widest text-groove-accent">Language</label>
                    <select className="w-full bg-[#0a081a] border border-white/10 rounded-[2rem] px-10 py-7 text-2xl text-white outline-none" value={persona.language} onChange={e => setPersona({...persona, language: e.target.value})}>
                        {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                </div>
                <div className="space-y-8">
                    <label className="text-[12px] font-black uppercase tracking-widest text-groove-accent">Accent</label>
                    <select className="w-full bg-[#0a081a] border border-white/10 rounded-[2rem] px-10 py-7 text-2xl text-white outline-none" value={persona.accent} onChange={e => setPersona({...persona, accent: e.target.value})}>
                        {ACCENTS.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>
                <div className="space-y-8">
                    <label className="text-[12px] font-black uppercase tracking-widest text-groove-accent">Vocal Tone</label>
                    <select className="w-full bg-[#0a081a] border border-white/10 rounded-[2rem] px-10 py-7 text-2xl text-white outline-none" value={persona.gender} onChange={e => setPersona({...persona, gender: e.target.value})}>
                        <option value="Female">Female</option>
                        <option value="Male">Male</option>
                    </select>
                </div>
             </div>
             <div className="space-y-10">
                <div className="flex flex-col gap-2">
                  <label className="text-[12px] font-black uppercase tracking-widest text-groove-accent">Create a detailed character, physical and background description</label>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">This is where the magic happens.</p>
                </div>
                <textarea rows={8} placeholder={t.purpose_placeholder} className="w-full bg-white/5 border border-white/10 rounded-[4rem] px-12 py-10 text-2xl text-white leading-relaxed resize-none outline-none shadow-inner focus:border-groove-pink transition-all" value={persona.background || ''} onChange={e => setPersona({...persona, background: e.target.value})} />
             </div>
          </div>
        )}

        {(step === 2 || step === 3) && (
           <div className="max-w-[1500px] mx-auto space-y-24 animate-in slide-in-from-right duration-1000">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
                 {Object.entries(step === 2 ? (t.trait_cats || {}) : (t.interest_cats || {})).map(([cat, items]: [string, any]) => (
                   <div key={cat} className="space-y-12">
                      <p className="text-[16px] font-black text-groove-accent uppercase tracking-[0.7em] border-l-8 border-groove-accent pl-8">{cat}</p>
                      <div className="flex flex-wrap gap-5">
                         {Array.isArray(items) && items.map((item: string) => {
                           const isSelected = (step === 2 ? persona.traits : persona.interests)?.includes(item);
                           return (
                             <button key={item} 
                               onClick={() => step === 2 ? (setPersona(prev => ({ ...prev, traits: isSelected ? prev.traits?.filter(t => t !== item) : [...(prev.traits || []), item] }))) : (setPersona(prev => ({ ...prev, interests: isSelected ? prev.interests?.filter(i => i !== item) : [...(prev.interests || []), item] })))} 
                               className={`px-10 py-5 rounded-[2rem] border-2 transition-all text-sm font-black uppercase tracking-widest ${ isSelected ? 'bg-groove-accent border-white text-black shadow-[0_0_50px_rgba(0,210,255,0.4)] scale-110' : 'bg-black/60 border-white/10 text-slate-500 hover:border-groove-pink hover:text-white' }`}>
                               {item}
                             </button>
                           );
                         })}
                      </div>
                   </div>
                 ))}
              </div>
           </div>
        )}

        {step === 4 && (
          <div className="max-w-4xl mx-auto space-y-20 text-center animate-in zoom-in duration-1000">
             <div className="relative w-80 h-80 mx-auto rounded-[5rem] overflow-hidden border-2 border-white/10 bg-black/50 group shadow-3xl">
                {persona.avatar ? (
                   <img src={persona.avatar} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" alt="Agent Manifestation" />
                ) : (
                   <div className="w-full h-full flex flex-col items-center justify-center opacity-10 space-y-4">
                      <i className="fa-solid fa-robot text-8xl"></i>
                      <p className="text-[10px] font-black uppercase tracking-[0.4em]">No Manifestation</p>
                   </div>
                )}
                {isGeneratingAvatar && (
                   <div className="absolute inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center">
                      <div className="w-16 h-16 border-4 border-groove-accent border-t-transparent rounded-full animate-spin"></div>
                   </div>
                )}
             </div>
             <div className="space-y-10">
                <h3 className="text-3xl font-black text-white uppercase tracking-[0.4em]">Neural Visualization</h3>
                <p className="text-2xl text-slate-400 font-light max-w-2xl mx-auto leading-relaxed">Execute a high-fidelity dream state to manifest {persona.name}'s visual profile based on established traits.</p>
                <button 
                   onClick={generateAvatar} 
                   disabled={isGeneratingAvatar || !apiKey} 
                   className="px-20 py-8 bg-groove-accent text-black font-black rounded-[2.5rem] uppercase tracking-widest text-[12px] shadow-3xl hover:scale-105 active:scale-95 transition-all disabled:opacity-20"
                >
                   {persona.avatar ? 'Sync New Manifestation' : ' dream manifestation'}
                </button>
             </div>
          </div>
        )}

        {step === 5 && (
          <div className="max-w-5xl mx-auto text-center space-y-20 py-20 animate-in zoom-in duration-1000">
             <div className="w-40 h-40 rounded-full bg-gradient-to-br from-groove-accent to-groove-pink mx-auto flex items-center justify-center shadow-[0_0_80px_rgba(255,0,210,0.3)] animate-pulse">
                <i className="fa-solid fa-shield-check text-6xl text-black"></i>
             </div>
             <div className="space-y-8">
                <h3 className="text-5xl font-black text-white uppercase tracking-[0.3em]">Calibration Finalized</h3>
                <p className="text-2xl text-slate-400 font-light leading-relaxed max-w-3xl mx-auto">All creative pathways are locked. Neural directives have been synchronized to your device's local vault. {persona.name} is ready for activation.</p>
             </div>
             <button onClick={() => onSave(persona as AIPersona, userProfile)} className="px-32 py-12 bg-white text-black font-black text-4xl rounded-[4rem] uppercase tracking-[0.6em] hover:bg-groove-pink hover:text-white transition-all shadow-3xl hover:scale-105 active:scale-95">
               INITIALIZE UPLINK
             </button>
          </div>
        )}
      </div>

      <div className="p-12 md:p-20 border-t border-white/10 flex justify-between items-center bg-black/60 backdrop-blur-2xl">
        <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0} className="px-16 py-8 text-slate-600 font-black uppercase tracking-[0.5em] text-sm disabled:opacity-0 hover:text-white transition-all">{common.back}</button>
        {step < 5 && <button onClick={() => setStep(s => s + 1)} className="px-24 py-8 bg-white text-black rounded-[2.5rem] font-black uppercase tracking-[0.5em] text-sm hover:bg-groove-pink hover:text-white transition-all shadow-2xl hover:scale-105 active:scale-95">{common.next}</button>}
      </div>
    </div>
  );
};

export default PersonaEngine;
